import {
	Col,
	Input,
	Modal,
	notification,
	Row,
	Splitter,
	TableProps,
} from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { Card, Empty } from 'antd';
import { ColumnsType, SorterResult } from 'antd/es/table/interface';
import { IVendorMaterial } from '../../../interfaces/Material.Interface';

import CustomResponsiveTable from '../../CustomResponsiveTable/index.tsx';
import { useGetVendorMaterialsQuery } from '../../../redux/api/vendor-materials.ts';
import { VendorMaterialFilter } from '../../../pages/Material';
import {
	useGetMaterialCategoriesQuery,
	useGetMaterialShapesQuery,
	useGetMaterialTypesQuery,
	useGetMaterialVendorsQuery,
} from '../../../redux/api/material.ts';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

interface IQueryParams {
	search?: string;
	page?: number;
	ordering?: string;
	availability_status?: string;
	vendors?: string;
	shape?: string;
	category?: string;
	types?: string;
	outer_diameter__gte?: string;
	outer_diameter__lte?: string;
	inner_diameter__gte?: string;
	inner_diameter__lte?: string;
	width__gte?: string;
	width__lte?: string;
	height__gte?: string;
	height__lte?: string;
	wall_thickness__gte?: string;
	wall_thickness__lte?: string;
}

interface Props {
	visible: boolean;
	onAddItem: (values: IVendorMaterial[]) => void;
	vendorPartsNumbers: string[];
	material_vendor_id: string;
	onCancel: (value: boolean) => void;
}

const AddFromVendorMaterials = (props: Props) => {
	const location = useLocation();
	const navigate = useNavigate();

	const [searchValue, setSearchValue] = useState<string>('');
	const queryParams = new URLSearchParams(location.search);
	const search = queryParams.get('search') || '';
	const currentPageUrl = Number(queryParams.get('page')) || 1;
	const ordering = queryParams.get('ordering') || '';
	const availability_status = queryParams.get('availability_status') || '';
	const vendors = queryParams.get('vendors') || '';
	const shape = queryParams.get('shape') || '';
	const category = queryParams.get('category') || '';
	const types = queryParams.get('types') || '';
	const outer_diameter__gte = queryParams.get('outer_diameter__gte') || '';
	const outer_diameter__lte = queryParams.get('outer_diameter__lte') || '';
	const inner_diameter__gte = queryParams.get('inner_diameter__gte') || '';
	const inner_diameter__lte = queryParams.get('inner_diameter__lte') || '';
	const width__gte = queryParams.get('width__gte') || '';
	const width__lte = queryParams.get('width__lte') || '';
	const height__gte = queryParams.get('height__gte') || '';
	const height__lte = queryParams.get('height__lte') || '';
	const wall_thickness__gte = queryParams.get('wall_thickness__gte') || '';
	const wall_thickness__lte = queryParams.get('wall_thickness__lte') || '';

	const vendorMaterialQuery: IQueryParams = {};

	if (search) {
		vendorMaterialQuery.search = search;
	}

	if (currentPageUrl) {
		vendorMaterialQuery.page = currentPageUrl;
	}

	if (ordering && ordering !== '') {
		vendorMaterialQuery.ordering = ordering;
	}

	if (availability_status && availability_status !== '') {
		vendorMaterialQuery.availability_status = availability_status;
	}

	if (props.material_vendor_id && props.material_vendor_id !== '') {
		vendorMaterialQuery.vendors = props.material_vendor_id;
	}

	if (shape && shape !== '') {
		vendorMaterialQuery.shape = shape;
	}

	if (category && category !== '') {
		vendorMaterialQuery.category = category;
	}

	if (types && types !== '') {
		vendorMaterialQuery.types = types;
	}

	if (outer_diameter__gte && outer_diameter__gte !== '') {
		vendorMaterialQuery.outer_diameter__gte = outer_diameter__gte;
	}

	if (outer_diameter__lte && outer_diameter__lte !== '') {
		vendorMaterialQuery.outer_diameter__lte = outer_diameter__lte;
	}

	if (inner_diameter__gte && inner_diameter__gte !== '') {
		vendorMaterialQuery.inner_diameter__gte = inner_diameter__gte;
	}

	if (inner_diameter__lte && inner_diameter__lte !== '') {
		vendorMaterialQuery.inner_diameter__lte = inner_diameter__lte;
	}

	if (width__gte && width__gte !== '') {
		vendorMaterialQuery.width__gte = width__gte;
	}

	if (width__lte && width__lte !== '') {
		vendorMaterialQuery.width__lte = width__lte;
	}

	if (height__gte && height__gte !== '') {
		vendorMaterialQuery.height__gte = height__gte;
	}

	if (height__lte && height__lte !== '') {
		vendorMaterialQuery.height__lte = height__lte;
	}

	if (wall_thickness__gte && wall_thickness__gte !== '') {
		vendorMaterialQuery.wall_thickness__gte = wall_thickness__gte;
	}

	if (wall_thickness__lte && wall_thickness__lte !== '') {
		vendorMaterialQuery.wall_thickness__lte = wall_thickness__lte;
	}

	const { data, isLoading, refetch } = useGetVendorMaterialsQuery(
		vendorMaterialQuery,
		{
			refetchOnFocus: true,
			refetchOnMountOrArgChange: true,
		}
	);

	const refetchData = () => {
		refetch();
	};

	const shapesQuery = useGetMaterialShapesQuery();
	const vendorsQuery = useGetMaterialVendorsQuery();
	const categoriesQuery = useGetMaterialCategoriesQuery();
	const typesQuery = useGetMaterialTypesQuery({ material_category: category });
	const [vendorMaterials, setVendorMaterials] = useState<IVendorMaterial[]>([]);
	const [currentPage, setCurrentPage] = useState<number>(currentPageUrl);
	const [totalRecords, setTotalRecords] = useState<number>(0);

	const pageChanged = async (page: number) => {
		queryParams.set('page', page.toString());
		navigate({ search: queryParams.toString() });
		setCurrentPage(page);
	};

	useEffect(() => {
		if (data) {
			setVendorMaterials(data.results as IVendorMaterial[]);
			setTotalRecords(data.count ? data.count : 0);
		}
	}, [data]);

	const getValue = (field: string, limit: number, dataField: string) => {
		if (limit) {
			return Number(limit);
		}
		if (data) {
			const results: any = data.results;
			const sorted = results
				.map((x: any) => x[field])
				.sort((a: any, b: any) => (dataField === 'gte' ? a - b : b - a));
			return Number(sorted[0]);
		}
	};

	useEffect(() => {
		if (search) {
			setSearchValue(search);
		}
	}, [search]);

	const handleTableChange: TableProps<IVendorMaterial>['onChange'] = (
		_pagination,
		_filters,
		sorter,
		extra
	) => {
		if (extra.action === 'sort') {
			const orderType = (sorter as SorterResult<IVendorMaterial>)?.order;
			const columnKey = (sorter as SorterResult<IVendorMaterial>)?.columnKey;
			const sorterColumn = (sorter as SorterResult<IVendorMaterial>)?.column;
			const column =
				orderType === 'ascend' || orderType === undefined
					? columnKey
					: '-' + columnKey;
			if (column !== undefined && sorterColumn !== undefined) {
				queryParams.set('ordering', column?.toString() as string);
			} else {
				queryParams.delete('ordering');
			}
			navigate({ search: queryParams.toString() });
		}
	};

	const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
	const [selectedRowObjects, setSelectedRowObjects] = useState<
		IVendorMaterial[]
	>([]);

	const rowSelection = {
		selectedRowKeys,
		onChange: (keys: React.Key[], selectedRows: IVendorMaterial[]) => {
			setSelectedRowKeys(keys);
			setSelectedRowObjects(selectedRows);
		},
		getCheckboxProps: (record: any) => ({
			disabled: record.availability_status !== 'Available',
		}),
	};

	const handleAddSelected = () => {
		if (selectedRowObjects.length > 0) {
			props.onAddItem(selectedRowObjects);
			props.onCancel(false);
		} else {
			notification.error({
				message: 'No material selected',
				description: 'Please select a material to add',
			});
		}
	};

	const columns: ColumnsType<any> = [
		{
			title: 'Vendor',
			key: 'vendor',
			render: (record: IVendorMaterial) => `${record?.vendor?.display_name}`,
		},
		{
			title: 'Material Type',
			key: 'material_type',
			render: (record: IVendorMaterial) => `${record?.type?.display_name}`,
		},
		{
			title: 'Material Shape',
			key: 'material_shape',
			render: (record: IVendorMaterial) => `${record?.shape?.display_name}`,
		},
		{
			title: 'Material Size',
			key: 'material_size',
			render: (record: IVendorMaterial) => {
				return `${record?.display_size}`;
			},
		},
		{
			title: 'Vendor Part #',
			render: (record: IVendorMaterial) =>
				`${record?.vendor_part_number ? record?.vendor_part_number : ''}`,
			key: 'vendor_part_number',
			sorter: false,
		},
		{
			title: 'Available',
			render: (record: IVendorMaterial) =>
				record.availability_status == 'Available' ? 'Yes' : 'No',
			key: 'availability_status',
			sorter: false,
		},
		{
			title: 'Lengths',
			render: (record: IVendorMaterial) => {
				return record.lengths.map((length) => Number(length.length)).join(', ');
			},
			sorter: false,
		},
	];

	const emptyTable = () => {
		return (
			<Empty
				image={Empty.PRESENTED_IMAGE_SIMPLE}
				description={
					<span style={{ color: '#000' }}>
						There are currently no materials to be displayed.
					</span>
				}
			/>
		);
	};

	useEffect(() => {
		if (props.visible) {
			queryParams.set('page', '1');
			queryParams.delete('search');
			queryParams.delete('ordering');
			queryParams.delete('availability_status');
			queryParams.delete('shape');
			queryParams.delete('category');
			queryParams.delete('types');
			navigate({ search: queryParams.toString() });
			setSelectedRowKeys([]);
			setSelectedRowObjects([]);
			setCurrentPage(1);
			setSearchValue('');
		}
	}, [props.visible]);

	return (
		<>
			<Modal
				title="Add From Vendor Materials"
				open={props.visible}
				onCancel={() => props.onCancel(false)}
				width={1600}
				centered
				okText="Add Selected"
				onOk={handleAddSelected}
			>
				<Row justify="space-between" className="mb-4">
					<Col span={4} className={'p-2'}>
						<Input.Search
							placeholder="Search"
							className="mb-2 w-full"
							allowClear={true}
							value={searchValue}
							onClear={() => {
								setSearchValue('');
								queryParams.delete('search');
								navigate({ search: queryParams.toString() });
							}}
							onChange={(e) => {
								setSearchValue(e.target.value);
							}}
							onSearch={(value) => {
								setSearchValue(value);
								queryParams.set('search', String(value));
								navigate({ search: queryParams.toString() });
							}}
						/>

						{shapesQuery.data && (
							<VendorMaterialFilter
								availability_status={
									availability_status ? availability_status : 'Available'
								}
								vendors={vendorsQuery.data ? vendorsQuery.data : []}
								vendors_selected={vendors}
								shapes={shapesQuery.data}
								shape_selected={shape}
								categories={categoriesQuery?.data ? categoriesQuery?.data : []}
								category_selected=""
								types={typesQuery.data ? typesQuery.data : []}
								types_selected={types}
								outer_diameter_gte={getValue(
									'outer_diameter',
									Number(outer_diameter__gte),
									'gte'
								)}
								outer_diameter_lte={getValue(
									'outer_diameter',
									Number(outer_diameter__lte),
									'lte'
								)}
								inner_diameter_gte={getValue(
									'inner_diameter',
									Number(inner_diameter__gte),
									'gte'
								)}
								inner_diameter_lte={getValue(
									'inner_diameter',
									Number(inner_diameter__lte),
									'lte'
								)}
								width_gte={getValue('width', Number(width__gte), 'gte')}
								width_lte={getValue('width', Number(width__lte), 'lte')}
								height_gte={getValue('height', Number(height__gte), 'gte')}
								height_lte={getValue('height', Number(height__lte), 'lte')}
								wall_thickness_gte={getValue(
									'wall_thickness',
									Number(wall_thickness__gte),
									'gte'
								)}
								wall_thickness_lte={getValue(
									'wall_thickness',
									Number(wall_thickness__lte),
									'lte'
								)}
								reset_url={
									'?availability_status=Available&shape=e7f36a73-d8e4-4bd1-acd9-6b024e666dac&page=1'
								}
							/>
						)}
					</Col>
					<Col span={20} className={'shadow-md rounded-lg border p-2 '}>
						<CustomResponsiveTable
							key={currentPage + '-' + selectedRowKeys.length}
							rowSelection={rowSelection}
							columns={columns}
							data={vendorMaterials}
							scroll={{ y: `calc(95vh - 250px)` }}
							emptyTable={emptyTable}
							loading={isLoading}
							onChange={handleTableChange}
							pagination={{
								pageSize: 20,
								current: currentPage,
								pageSizeOptions: [],
								total: totalRecords,
								onChange: async (page) => {
									setCurrentPage(page);
									await pageChanged(page);
								},
								defaultPageSize: 20,
							}}
						/>
					</Col>
				</Row>
			</Modal>
		</>
	);
};

export default AddFromVendorMaterials;
